import React, { HTMLAttributes, forwardRef } from 'react';
import { cn } from 'utils/helpers';

interface GridProps extends HTMLAttributes<HTMLDivElement> {
  cols?: 1 | 2 | 3 | 4 | 6 | 12;
  gap?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  responsive?: boolean;
}

export const Grid = forwardRef<HTMLDivElement, GridProps>(
  ({ children, className, cols = 3, gap = 'md', responsive = true, ...props }, ref) => {
    const colsMap = {
      1: 'grid-cols-1',
      2: responsive ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2',
      3: responsive ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-3',
      4: responsive ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-4',
      6: responsive ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6' : 'grid-cols-6',
      12: responsive ? 'grid-cols-4 sm:grid-cols-6 lg:grid-cols-12' : 'grid-cols-12',
    };

    const gapMap = {
      none: 'gap-0',
      sm: 'gap-2',
      md: 'gap-4',
      lg: 'gap-6',
      xl: 'gap-8',
    };

    return (
      <div
        ref={ref}
        className={cn('grid', colsMap[cols], gapMap[gap], className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Grid.displayName = 'Grid';

interface GridItemProps extends HTMLAttributes<HTMLDivElement> {
  colSpan?: 1 | 2 | 3 | 4 | 6 | 12 | 'full';
}

export const GridItem = forwardRef<HTMLDivElement, GridItemProps>(
  ({ children, className, colSpan = 1, ...props }, ref) => {
    const colSpanMap = {
      1: 'col-span-1',
      2: 'col-span-full sm:col-span-2',
      3: 'col-span-full sm:col-span-2 lg:col-span-3',
      4: 'col-span-full sm:col-span-2 lg:col-span-4',
      6: 'col-span-full sm:col-span-3 lg:col-span-6',
      12: 'col-span-full',
      full: 'col-span-full',
    };

    return (
      <div ref={ref} className={cn(colSpanMap[colSpan], className)} {...props}>
        {children}
      </div>
    );
  }
);

GridItem.displayName = 'GridItem';

